(() => {
  const MQ = window.matchMedia('(min-width: 990px)');
  const LAYOUT_SELECTOR = '.js-product-layout';
  const TARGET_SELECTOR = '.js-product-side-inner';

  const root = document.documentElement;

  const getHeaderOffset = () => {
    const v = getComputedStyle(root).getPropertyValue('--header-height').trim();
    if (v) {
      const n = parseFloat(v.replace('px', ''));
      if (!Number.isNaN(n)) return n;
    }
    const header = document.querySelector('.js-header, header, .header');
    return header ? header.getBoundingClientRect().height : 0;
  };

  const clamp = (val, min, max) => Math.max(min, Math.min(max, val));

  class SmartSticky {
    constructor({ container, target, topGap = 16, bottomGap = 16, deadzone = 2 }) {
      this.container = container;
      this.target = target;

      this.topGap = topGap;
      this.bottomGap = bottomGap;
      this.deadzone = deadzone;

      this.raf = 0;
      this.ro = null;

      this.scrollY = window.scrollY;
      this.lastScrollY = this.scrollY;

      this.y = 0;
      this.prevY = null;

      // JSで動かすので sticky は殺す
      this.target.style.position = 'relative';
      this.target.style.top = '0';
      this.target.style.transform = 'translate3d(0,0,0)';
      this.target.style.willChange = 'transform';

      this.onScroll = this.onScroll.bind(this);
      this.onResize = this.onResize.bind(this);

      this.measure();
      this.bind();
      this.update(true);
    }

    bind() {
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onResize, { passive: true });

      if ('ResizeObserver' in window) {
        this.ro = new ResizeObserver(() => {
          // 高さが変わった時だけ再計測→反映
          this.measure();
          this.update(true);
        });
        this.ro.observe(this.container);
        this.ro.observe(this.target);
      } else {
        window.addEventListener('load', this.onResize);
      }
    }

    destroy() {
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onResize);
      window.removeEventListener('load', this.onResize);

      if (this.ro) {
        this.ro.disconnect();
        this.ro = null;
      }

      if (this.raf) {
        cancelAnimationFrame(this.raf);
        this.raf = 0;
      }

      this.target.style.transform = '';
      this.target.style.willChange = '';
      this.target.style.position = '';
      this.target.style.top = '';
    }

    measure() {
      this.header = getHeaderOffset();

      const crect = this.container.getBoundingClientRect();
      this.cTop = crect.top + window.scrollY;

      this.cH = this.container.offsetHeight;
      this.tH = this.target.offsetHeight;
      this.vH = window.innerHeight;

      this.topLimit = this.header + this.topGap;
      this.bottomLimit = this.bottomGap;

      this.maxY = Math.max(0, this.cH - this.tH);

      // 範囲外に飛んでたら戻す
      this.y = clamp(this.y, 0, this.maxY);
      if (this.prevY !== null) this.prevY = clamp(this.prevY, 0, this.maxY);
    }

    onResize() {
      this.measure();
      this.update(true);
    }

    onScroll() {
      // scrollY はここでキャッシュ（update内で読まない）
      this.scrollY = window.scrollY;

      if (this.raf) return;
      this.raf = requestAnimationFrame(() => {
        this.raf = 0;
        this.update(false);
      });
    }

    update(force) {
      const scrollY = this.scrollY;
      const delta = scrollY - this.lastScrollY;
      this.lastScrollY = scrollY;

      const viewTopInC = scrollY + this.topLimit - this.cTop;
      const viewBottomInC = scrollY + this.vH - this.bottomLimit - this.cTop;

      let nextY = this.y;

      // container 範囲外は固定
      if (viewTopInC <= 0) {
        nextY = 0;
      } else if (viewBottomInC >= this.cH) {
        nextY = this.maxY;
      } else {
        const availableVH = this.vH - this.topLimit - this.bottomLimit;

        if (this.tH <= availableVH) {
          // 収まる → 上基準で追従
          nextY = clamp(viewTopInC, 0, this.maxY);
        } else {
          // 長い → smart追従（方向に合わせて「端」を追う）
          if (delta > 0) {
            const desired = viewBottomInC - this.tH;
            nextY = clamp(Math.max(nextY, desired), 0, this.maxY);
          } else if (delta < 0) {
            const desired = viewTopInC;
            nextY = clamp(Math.min(nextY, desired), 0, this.maxY);
          }
        }
      }

      // 微振動・残像対策：整数化 + デッドゾーン
      nextY = Math.round(nextY);

      if (!force && this.prevY !== null) {
        const diff = Math.abs(nextY - this.prevY);
        if (diff < this.deadzone) {
          this.y = nextY;
          return;
        }
      }

      if (!force && this.prevY === nextY) {
        this.y = nextY;
        return;
      }

      this.y = nextY;
      this.prevY = nextY;

      // 書き込みは最後に1回だけ
      this.target.style.transform = `translate3d(0, ${nextY}px, 0)`;
    }
  }

  let instance = null;

  const mount = () => {
    const container = document.querySelector(LAYOUT_SELECTOR);
    const target = document.querySelector(TARGET_SELECTOR);
    if (!container || !target) return;

    // 既存があれば破棄（Shopify再描画対策）
    if (instance) {
      instance.destroy();
      instance = null;
    }

    instance = new SmartSticky({
      container,
      target,
      topGap: 16,
      bottomGap: 16,
      deadzone: 2, // ここが残像を殺す
    });
  };

  const unmount = () => {
    if (!instance) return;
    instance.destroy();
    instance = null;
  };

  const sync = () => {
    if (MQ.matches) mount();
    else unmount();
  };

  // ブレイクポイント切替
  if (MQ.addEventListener) MQ.addEventListener('change', sync);
  else MQ.addListener(sync);

  // 初期
  const init = () => sync();

  // Shopifyの再描画系
  document.addEventListener('DOMContentLoaded', init);
  window.addEventListener('load', init);
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:reorder', init);
  document.addEventListener('shopify:section:select', init);
})();
